import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { APP_VERSION } from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/format";
import { RefreshCw, Download, CheckCircle2, AlertTriangle, Info, XCircle, Loader2, Lock, PackageCheck, Package, ChevronDown, ChevronRight, ExternalLink, Undo2, Terminal, KeyRound } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MSG_ICON = {
  success: <CheckCircle2 size={14} className="text-emerald-600" />,
  info: <Info size={14} className="text-sky-600" />,
  warn: <AlertTriangle size={14} className="text-amber-600" />,
  error: <XCircle size={14} className="text-red-600" />,
};

const MD_COMPONENTS = {
  h1: ({ node, ...p }) => <h1 className="mb-2 mt-4 text-base font-bold text-zinc-900 first:mt-0" {...p} />,
  h2: ({ node, ...p }) => <h2 className="mb-1.5 mt-4 text-sm font-bold uppercase tracking-wide text-zinc-800 first:mt-0" {...p} />,
  h3: ({ node, ...p }) => <h3 className="mb-1 mt-3 text-sm font-semibold text-zinc-900" {...p} />,
  p: ({ node, ...p }) => <p className="my-2" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-2 list-disc space-y-1 pl-5" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-2 list-decimal space-y-1 pl-5" {...p} />,
  li: ({ node, ...p }) => <li className="marker:text-zinc-400" {...p} />,
  a: ({ node, ...p }) => <a target="_blank" rel="noreferrer" className="text-primary hover:underline" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold text-zinc-900" {...p} />,
  code: ({ node, ...p }) => <code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[12px] text-zinc-800" {...p} />,
  hr: ({ node, ...p }) => <hr className="my-3 border-zinc-100" {...p} />,
  blockquote: ({ node, ...p }) => <blockquote className="my-2 border-l-2 border-zinc-200 pl-3 text-zinc-500" {...p} />,
  table: ({ node, ...p }) => <table className="my-2 w-full border-collapse text-xs" {...p} />,
  th: ({ node, ...p }) => <th className="border border-zinc-200 bg-zinc-50 px-2 py-1 text-left font-semibold" {...p} />,
  td: ({ node, ...p }) => <td className="border border-zinc-200 px-2 py-1" {...p} />,
};

function VersionCard({ label, value, testid, icon: Icon }) {
  return (
    <div className="border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className="mt-1 font-mono text-lg font-bold text-zinc-900">{value || "—"}</div>
    </div>
  );
}

export function UpdatesPanel({ expired }) {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [logText, setLogText] = useState("");
  const [reg, setReg] = useState({ server: "ghcr.io", username: "", token: "" });
  const [showReg, setShowReg] = useState(false);

  const fetchLog = () => api.get("/v1/system/update-log").then((r) => setLogText(r.data.log || "")).catch(() => {});

  const load = (spin) => {
    if (spin) setChecking(true);
    return api.get("/v1/system/update-check")
      .then((r) => setD(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setChecking(false));
  };
  useEffect(() => { load(false); }, []);

  // Stream the live self-update log + refresh status while an update is in flight.
  useEffect(() => {
    if (!d?.pending) return;
    fetchLog();
    const iv = setInterval(() => { fetchLog(); load(false); }, 3000);
    return () => clearInterval(iv);
  }, [d?.pending?.startedAt]);

  useEffect(() => {
    if (d) setReg((p) => ({ ...p, server: d.registryServer || "ghcr.io", username: d.registryUsername || "" }));
  }, [d?.registryServer, d?.registryUsername]);

  const toggleAuto = async (v) => {
    try {
      await api.post("/v1/system/update-config", { autoUpdate: v });
      setD((p) => ({ ...p, autoUpdate: v }));
      toast.success(v ? t("upd.autoOn") : t("upd.autoOff"));
      load(false);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doUpdate = async () => {
    setUpdating(true);
    try {
      const r = await api.post("/v1/system/update-trigger");
      toast.success(t("upd.triggered").replace("{v}", r.data.version));
      fetchLog();
      load(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUpdating(false); }
  };

  const doRollback = async () => {
    if (!window.confirm(t("upd.rollbackConfirm").replace("{v}", d.rollbackTarget || ""))) return;
    setUpdating(true);
    try {
      const r = await api.post("/v1/system/update-rollback");
      toast.success(t("upd.rollbackStarted").replace("{v}", r.data.version));
      fetchLog();
      load(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUpdating(false); }
  };

  const saveReg = async () => {
    try {
      const r = await api.post("/v1/system/update-registry", reg);
      toast.success(r.data.configured ? t("upd.regSaved") : t("upd.regCleared"));
      setReg((p) => ({ ...p, token: "" }));
      load(false);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <div className="p-8 font-mono text-xs uppercase tracking-widest text-zinc-400" data-testid="updates-loading">{t("common.loading")}</div>;

  const canUpdate = d.canAutoUpdate && !expired;

  return (
    <div className="space-y-6" data-testid="updates-panel">
      <div>
        <h3 className="text-lg font-bold text-zinc-900">{t("upd.title")}</h3>
        <p className="mt-0.5 text-sm text-zinc-500">{t("upd.subtitle")}</p>
      </div>

      {expired && (
        <div className="flex items-center gap-2 border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="updates-readonly-banner">
          <Lock size={15} /> {t("upd.readonly")}
        </div>
      )}

      {d.pending && (
        <div className="flex items-center gap-2 border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-medium text-sky-800" data-testid="updates-verifying-banner">
          <Loader2 size={15} className="animate-spin" /> {t("upd.verifying").replace("{v}", d.pending.target || "")}
        </div>
      )}

      {/* Installed */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <VersionCard label={t("upd.apiVersion")} value={d.currentVersion} testid="updates-api-version" icon={Package} />
        <VersionCard label={t("upd.uiVersion")} value={APP_VERSION} testid="updates-ui-version" icon={Package} />
        <VersionCard label={t("upd.buildDate")} value={d.buildDate ? fmtDate(d.buildDate) : t("upd.unknown")} testid="updates-build-date" icon={PackageCheck} />
      </div>

      {/* Latest / action */}
      <div className="border border-zinc-200 bg-white p-5" data-testid="updates-latest">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("upd.latest")}</div>
            {d.error ? (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-amber-700" data-testid="updates-error">
                <AlertTriangle size={15} /> {d.reason || d.error}
              </div>
            ) : d.updateAvailable ? (
              <>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-mono text-xl font-bold text-zinc-900" data-testid="updates-latest-version">{d.latestVersion}</span>
                  <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700" data-testid="updates-available-badge">{t("upd.available")}</span>
                </div>
                {d.latestPublishedAt && <div className="mt-0.5 font-mono text-[11px] text-zinc-400">{t("upd.published")}: {fmtDate(d.latestPublishedAt)}</div>}
                <p className={`mt-2 max-w-lg text-sm ${canUpdate ? "text-zinc-600" : "text-amber-700"}`} data-testid="updates-reason">{d.reason}</p>
              </>
            ) : (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-emerald-700" data-testid="updates-uptodate">
                <CheckCircle2 size={15} /> {t("upd.upToDate")}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={() => load(true)} disabled={checking} data-testid="updates-check-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50">
              {checking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {checking ? t("upd.checking") : t("upd.check")}
            </button>
            {d.updateAvailable && (
              <button onClick={doUpdate} disabled={!canUpdate || updating} data-testid="updates-trigger-btn"
                title={!canUpdate ? d.reason : ""}
                className="inline-flex items-center gap-1.5 border border-zinc-300 bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002480] disabled:cursor-not-allowed disabled:opacity-50">
                {updating ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {updating ? t("upd.updating") : t("upd.update")}
              </button>
            )}
          </div>
        </div>
        {!d.selfUpdateAvailable && (
          <div className="mt-3 border-t border-zinc-100 pt-3" data-testid="updates-selfupdate-warn">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600">{t("upd.selfUpdateMissing")}</div>
            <ul className="mt-2 space-y-1.5 text-xs text-zinc-600">
              {[
                [d.selfUpdateDiag?.dockerSocket, t("upd.diagSocket"), t("upd.diagSocketHint")],
                [d.selfUpdateDiag?.dockerCli, t("upd.diagCli"), t("upd.diagCliHint")],
                [d.selfUpdateDiag?.composeFile, t("upd.diagCompose"), (t("upd.diagComposeHint") || "").replace("{dir}", d.selfUpdateDiag?.composeDir || "/app/config")],
              ].map(([ok, label, hint], i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`updates-diag-row-${i}`}>
                  {ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-500" /> : <XCircle size={14} className="mt-0.5 shrink-0 text-red-500" />}
                  <span><span className={ok ? "text-zinc-700" : "font-semibold text-zinc-800"}>{label}</span>{!ok && hint ? <span className="text-zinc-500"> — {hint}</span> : null}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Live self-update log */}
      {(d.pending || logText) && (
        <div className="border border-zinc-200 bg-white" data-testid="updates-log">
          <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-600">
              <Terminal size={14} /> {t("upd.liveLog")}
              {d.pending && <Loader2 size={12} className="animate-spin text-sky-600" />}
            </div>
            <button onClick={fetchLog} data-testid="updates-log-refresh" className="text-[11px] font-semibold text-primary hover:underline">{t("upd.refreshLog")}</button>
          </div>
          <pre className="max-h-72 overflow-auto bg-zinc-950 px-4 py-3 font-mono text-[11px] leading-relaxed text-zinc-100" data-testid="updates-log-body">
{logText || t("upd.logEmpty")}
          </pre>
        </div>
      )}

      {/* Release notes / changelog */}
      {d.updateAvailable && d.releaseNotes && (
        <div className="border border-zinc-200 bg-white" data-testid="updates-release-notes">
          <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3">
            <button onClick={() => setShowNotes((v) => !v)} data-testid="updates-notes-toggle"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900">
              {showNotes ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {t("upd.whatsNew")} · {d.latestVersion}
            </button>
            {d.releaseUrl && (
              <a href={d.releaseUrl} target="_blank" rel="noreferrer" data-testid="updates-notes-link"
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                {t("upd.viewOnGithub")} <ExternalLink size={12} />
              </a>
            )}
          </div>
          {showNotes && (
            <div className="max-h-80 overflow-auto px-5 py-4 text-sm leading-relaxed text-zinc-700" data-testid="updates-notes-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
                {d.releaseNotes}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/* One-click rollback */}
      {d.rollbackTarget && (
        <div className="flex flex-wrap items-center justify-between gap-3 border border-zinc-200 bg-white p-4" data-testid="updates-rollback">
          <div className="pr-4">
            <div className="text-sm font-bold text-zinc-900">{t("upd.rollbackTitle")}</div>
            <div className="text-sm text-zinc-500">{t("upd.rollbackDesc").replace("{v}", d.rollbackTarget)}</div>
          </div>
          <button onClick={doRollback} disabled={!d.rollbackAvailable || expired || updating || !!d.pending} data-testid="updates-rollback-btn"
            title={!d.rollbackAvailable ? t("upd.selfUpdateMissing") : ""}
            className="inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50">
            {updating ? <Loader2 size={15} className="animate-spin" /> : <Undo2 size={15} />} {t("upd.rollbackBtn").replace("{v}", d.rollbackTarget)}
          </button>
        </div>
      )}

      {/* Private registry login (optional — for private image pulls, e.g. GHCR) */}
      <div className="border border-zinc-200 bg-white" data-testid="updates-registry">
        <button onClick={() => setShowReg((v) => !v)} data-testid="updates-registry-toggle"
          className="flex w-full items-center justify-between px-4 py-3 text-left">
          <div className="inline-flex items-center gap-1.5 text-sm font-bold text-zinc-900">
            <KeyRound size={15} /> {t("upd.regTitle")}
            {d.registryConfigured && <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700" data-testid="updates-registry-configured">{t("upd.regConfigured")}</span>}
          </div>
          {showReg ? <ChevronDown size={16} className="text-zinc-400" /> : <ChevronRight size={16} className="text-zinc-400" />}
        </button>
        {showReg && (
          <div className="space-y-3 border-t border-zinc-100 px-4 py-4">
            <p className="text-xs text-zinc-500">{t("upd.regDesc")}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="text-xs font-semibold text-zinc-600">{t("upd.regServer")}
                <input value={reg.server} onChange={(e) => setReg({ ...reg, server: e.target.value })} data-testid="updates-registry-server" placeholder="ghcr.io"
                  className="mt-1 w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm text-zinc-900" />
              </label>
              <label className="text-xs font-semibold text-zinc-600">{t("upd.regUser")}
                <input value={reg.username} onChange={(e) => setReg({ ...reg, username: e.target.value })} data-testid="updates-registry-user"
                  className="mt-1 w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm text-zinc-900" />
              </label>
              <label className="text-xs font-semibold text-zinc-600">{t("upd.regToken")}
                <input type="password" value={reg.token} onChange={(e) => setReg({ ...reg, token: e.target.value })} data-testid="updates-registry-token"
                  placeholder={d.registryConfigured ? "••••••••" : ""}
                  className="mt-1 w-full rounded-sm border border-zinc-300 px-3 py-2 font-mono text-sm text-zinc-900" />
              </label>
            </div>
            <div className="flex justify-end">
              <button onClick={saveReg} data-testid="updates-registry-save"
                className="inline-flex items-center gap-1.5 border border-zinc-300 bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#002480]">
                {t("upd.regSave")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Auto-update */}
      <div className="flex items-center justify-between border border-zinc-200 bg-white p-4" data-testid="updates-auto">
        <div className="pr-4">
          <div className="text-sm font-bold text-zinc-900">{t("upd.autoUpdate")}</div>
          <div className="text-sm text-zinc-500">{t("upd.autoUpdateDesc")}</div>
        </div>
        <Switch checked={!!d.autoUpdate} onCheckedChange={toggleAuto} disabled={expired} data-testid="updates-auto-switch" />
      </div>

      {/* Messages */}
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("upd.messages")}</div>
        <div className="border border-zinc-200 bg-white" data-testid="updates-messages">
          {(d.messages || []).length === 0 ? (
            <div className="p-5 text-center text-sm text-zinc-400" data-testid="updates-no-messages">{t("upd.noMessages")}</div>
          ) : (
            <div className="max-h-72 divide-y divide-zinc-100 overflow-auto">
              {d.messages.map((m, i) => (
                <div key={i} className="flex items-start gap-2 px-4 py-2.5 text-sm" data-testid="updates-message-row">
                  <span className="mt-0.5 shrink-0">{MSG_ICON[m.level] || MSG_ICON.info}</span>
                  <div className="min-w-0 flex-1">
                    <span className="text-zinc-800">{m.message}</span>
                    <div className="font-mono text-[10px] text-zinc-400">{fmtDate(m.ts)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
